import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getCourse, updateCourse,
  createModule, updateModule,
  createAssignment, updateAssignment,
} from '../../api/instructorApi';
import Toast from '../../components/admin/Toast';
import PageLoader from '../../components/common/PageLoader';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function InstructorCourse() {
  const { courseId } = useParams();
  const navigate     = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';

  const [course,      setCourse]      = useState(null);
  const [modules,     setModules]     = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [enrolledCount, setEnrolledCount] = useState(0);
  const [toast,       setToast]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [form,        setForm]        = useState({ title: '', description: '' });
  const [modal,       setModal]       = useState(null); // 'addModule' | 'addAssignment' | 'editAssignment'
  const [moduleForm,  setModuleForm]  = useState({ title: '', overview: '' });
  const [assignForm,  setAssignForm]  = useState({ title: '', description: '', link: '', due_date: '', max_score: 100, accepted_file_types: 'PDF,DOCX' });
  const [selAssign,   setSelAssign]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getCourse(Number(courseId)).then(d => {
      setCourse(d.course);
      setModules(d.modules);
      setAssignments(d.assignments);
      setEnrolledCount(d.enrolled_count);
      setForm({ title: d.course.title, description: d.course.description });
    }).catch(console.error).finally(() => setLoading(false));
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  function setTab(t) { setSearchParams({ tab: t }); }

  async function handleSaveCourse(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await updateCourse(Number(courseId), form);
      load();
      setToast({ msg: 'Course saved', type: 'success' });
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleAddModule(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await createModule(Number(courseId), moduleForm);
      load();
      setModal(null);
      setModuleForm({ title: '', overview: '' });
      setToast({ msg: 'Module created', type: 'success' });
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally { setSaving(false); }
  }

  async function handlePublishModule(moduleId, currentStatus) {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    try {
      await updateModule(Number(courseId), moduleId, { status: newStatus });
      load();
      setToast({ msg: `Module ${newStatus === 'published' ? 'published' : 'set to draft'}`, type: 'success' });
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    }
  }

  async function handleSaveAssignment(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...assignForm,
        accepted_file_types: assignForm.accepted_file_types.split(',').map(s => s.trim()).filter(Boolean),
        max_score: Number(assignForm.max_score),
      };
      if (selAssign) {
        await updateAssignment(Number(courseId), selAssign.id, body);
      } else {
        await createAssignment(Number(courseId), body);
      }
      load();
      setModal(null);
      setSelAssign(null);
      setToast({ msg: selAssign ? 'Assignment updated' : 'Assignment created', type: 'success' });
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally { setSaving(false); }
  }

  async function handlePublishAssignment(aId, currentStatus) {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    try {
      await updateAssignment(Number(courseId), aId, { status: newStatus });
      load();
      setToast({ msg: `Assignment ${newStatus}`, type: 'success' });
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    }
  }

  function openEditAssignment(a) {
    setSelAssign(a);
    setAssignForm({
      title: a.title,
      description: a.description || '',
      link: a.link || '',
      due_date: a.due_date || '',
      max_score: a.max_score,
      accepted_file_types: (a.accepted_file_types || []).join(', '),
    });
    setModal('editAssignment');
  }

  function openAddAssignment() {
    setSelAssign(null);
    setAssignForm({ title: '', description: '', link: '', due_date: '', max_score: 100, accepted_file_types: 'PDF,DOCX' });
    setModal('addAssignment');
  }

  if (loading || !course) return <div className="body"><PageLoader /></div>;

  const pubMods = modules.filter(m => m.status === 'published').length;

  return (
    <>
      {/* ── Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Instructor / Course Management</div>
          <div className="pt">{course.title}</div>
          <div className="ps">
            {enrolledCount} students · {modules.length} modules · {assignments.length} assignments
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-o btn-sm" onClick={() => navigate('/instructor/dashboard')}>← Dashboard</button>
          <button className="btn btn-o btn-sm" onClick={() => navigate(`/instructor/courses/${courseId}/students`)}>Students</button>
        </div>
      </div>

      <div className="body">
        {/* ── Tab Bar ── */}
        <div className="tab-bar">
          {[
            { key: 'overview',     icon: '◎', label: 'Overview' },
            { key: 'modules',      icon: '⊞', label: 'Modules' },
            { key: 'assignments',  icon: '📋', label: 'Assignments' },
          ].map(t => (
            <button
              key={t.key}
              className={`tab${tab === t.key ? ' on' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ── Overview Tab ── */}
        {tab === 'overview' && (
          <>
            <div className="stats" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
              <div className="sc">
                <div className="sc-l">Modules</div>
                <div className="sc-v">{pubMods}/{modules.length}</div>
                {modules.length > 0 && (
                  <div className="sc-bar">
                    <div className="sc-fill" style={{ width: `${Math.round(pubMods / modules.length * 100)}%`, background: 'var(--yellow)' }} />
                  </div>
                )}
              </div>
              <div className="sc">
                <div className="sc-l">Students</div>
                <div className="sc-v">{enrolledCount}</div>
                <div className="sc-s" style={{ color: 'var(--green)' }}>Active</div>
              </div>
              <div className="sc">
                <div className="sc-l">Assignments</div>
                <div className="sc-v">{assignments.length}</div>
                <div className="sc-s" style={{ color: 'var(--muted)' }}>
                  {assignments.filter(a => a.status === 'published').length} published
                </div>
              </div>
              <div className="sc">
                <div className="sc-l">Status</div>
                <div className="sc-v" style={{ fontSize: 16 }}>
                  <span className={`b ${course.status === 'Published' ? 'b-g' : 'b-o'}`}>
                    {course.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="ct">Course Details</div>
              <form onSubmit={handleSaveCourse}>
                <div className="fg">
                  <label className="fl">Course Title</label>
                  <input className="li" value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
                </div>
                <div className="fg" style={{ marginBottom: 0 }}>
                  <label className="fl">Course Description</label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={form.description || ''}
                    onChange={(_, editor) => setForm(f => ({ ...f, description: editor.getData() }))}
                    config={{ toolbar: ['bold','italic','|','bulletedList','numberedList','|','blockQuote','link'] }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                  <button type="submit" className="btn btn-p btn-sm" disabled={saving}>
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}

        {/* ── Modules Tab ── */}
        {tab === 'modules' && (
          <div className="card">
            <div className="ct">
              <span>Modules</span>
              <button className="btn btn-o btn-sm" onClick={() => { setModuleForm({ title: '', overview: '' }); setModal('addModule'); }}>
                + Add Module
              </button>
            </div>
            {modules.length === 0 ? (
              <div className="empty">
                <div className="empty-icon">⊞</div>
                <div>No modules yet</div>
              </div>
            ) : (
              modules.map((m, i) => {
                const checks = [
                  m.video_uploaded ? '🎬' : '',
                  m.presentation_uploaded ? '📑' : '',
                  m.question_count > 0 ? '📝' : '',
                  m.resource_count > 0 ? '📎' : '',
                ].filter(Boolean).join(' ');
                return (
                  <div key={m.id} className="mi">
                    <div className={`mn${m.status === 'published' ? ' done' : ''}`}>
                      {m.status === 'published' ? '✓' : i + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="mt">{m.title}</div>
                      <div className="ms">
                        {checks || 'No content yet'} · {m.question_count} quiz questions · {m.resource_count} resources
                      </div>
                    </div>
                    <span className={`b ${m.status === 'published' ? 'b-g' : 'b-o'}`}>
                      {m.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                    <button
                      className="btn btn-o btn-sm"
                      onClick={() => navigate(`/instructor/courses/${courseId}/modules/${m.id}`)}
                    >
                      Edit →
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── Assignments Tab ── */}
        {tab === 'assignments' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontFamily: "'Nunito Sans',sans-serif", fontWeight: 700, fontSize: 14 }}>
                Course Assignments <span className="b b-y">{assignments.length} total</span>
              </div>
              <button className="btn btn-dark btn-sm" onClick={openAddAssignment}>+ New Assignment</button>
            </div>

            {assignments.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: 44 }}>
                <div style={{ fontSize: 44, marginBottom: 14 }}>📋</div>
                <div style={{ fontFamily: "'Nunito Sans',sans-serif", fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
                  No assignments yet
                </div>
                <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18, maxWidth: 340, margin: '0 auto 18px' }}>
                  Create assignments for students to submit their work.
                </p>
                <button className="btn btn-dark btn-sm" onClick={openAddAssignment}>+ Create First Assignment</button>
              </div>
            ) : (
              assignments.map((a, i) => (
                <div key={a.id} className="card" style={{ marginBottom: 14 }}>
                  <div className="ct">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>📋</span>
                      <span>{a.title}</span>
                    </div>
                    <span className={`b ${a.status === 'published' ? 'b-g' : 'b-o'}`}>
                      {a.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  {a.description && (
                    <div className="rich-text" style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.65, marginBottom: 14 }}
                      dangerouslySetInnerHTML={{ __html: a.description }} />
                  )}
                  {a.link && (
                    <div style={{ marginBottom: 14 }}>
                      <a href={a.link} target="_blank" rel="noreferrer"
                        style={{ fontSize: 13, color: '#1A56DB', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                        🔗 Reference Link ↗
                      </a>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 }}>
                    {[
                      { label: 'Due Date',    value: fmtDate(a.due_date) },
                      { label: 'Submissions', value: `${a.submissions_count} / ${a.total_enrolled}` },
                      { label: 'Graded',      value: `${a.graded_count} / ${a.submissions_count}` },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ padding: '10px 12px', background: 'var(--surface)', borderRadius: 'var(--rs)', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 3 }}>
                          {label}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      <span className="uz-tag">Accepted: {(a.accepted_file_types || []).join(', ')}</span>
                      <span className="uz-tag">Max {a.max_score} pts</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-o btn-sm" onClick={() => openEditAssignment(a)}>Edit</button>
                      <button
                        className="btn btn-dark btn-sm"
                        onClick={() => navigate(`/instructor/courses/${courseId}/assignments/${a.id}/submissions`)}
                      >
                        View Submissions →
                      </button>
                      {a.status !== 'published' && (
                        <button className="btn btn-p btn-sm" onClick={() => handlePublishAssignment(a.id, a.status)}>
                          Publish
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      {/* ── Add Module Modal ── */}
      {modal === 'addModule' && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Add Module
              <button className="btn btn-o btn-sm" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={handleAddModule}>
              <div className="fg">
                <label className="fl">Module Title</label>
                <input className="li" placeholder="e.g. Introduction to AI" required
                  value={moduleForm.title}
                  onChange={e => setModuleForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="fg" style={{ marginBottom: 0 }}>
                <label className="fl">Overview <span style={{ color: 'var(--subtle)', fontWeight: 400 }}>(optional)</span></label>
                <CKEditor
                  editor={ClassicEditor}
                  data={moduleForm.overview || ''}
                  onChange={(_, editor) => setModuleForm(f => ({ ...f, overview: editor.getData() }))}
                  config={{ toolbar: ['bold','italic','|','bulletedList','numberedList','|','blockQuote','link'] }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button type="button" className="btn btn-o" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-p" disabled={saving}>{saving ? 'Creating…' : 'Create Module'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add/Edit Assignment Modal ── */}
      {(modal === 'addAssignment' || modal === 'editAssignment') && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              {selAssign ? 'Edit Assignment' : 'New Assignment'}
              <button className="btn btn-o btn-sm" onClick={() => { setModal(null); setSelAssign(null); }}>✕</button>
            </div>
            <form onSubmit={handleSaveAssignment}>
              <div className="fg">
                <label className="fl">Title</label>
                <input className="li" required placeholder="Assignment title"
                  value={assignForm.title}
                  onChange={e => setAssignForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="fg">
                <label className="fl">Description</label>
                <CKEditor
                  key={selAssign?.id ?? 'new'}
                  editor={ClassicEditor}
                  data={assignForm.description || ''}
                  onChange={(_, editor) => setAssignForm(f => ({ ...f, description: editor.getData() }))}
                  config={{ toolbar: ['bold','italic','|','bulletedList','numberedList','|','blockQuote','link'] }}
                />
              </div>
              <div className="fg">
                <label className="fl">Reference Link <span style={{ color: 'var(--subtle)', fontWeight: 400 }}>(optional)</span></label>
                <input className="li" type="url" placeholder="https://…"
                  value={assignForm.link}
                  onChange={e => setAssignForm(f => ({ ...f, link: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="fg">
                  <label className="fl">Due Date</label>
                  <input className="li" type="date" required
                    value={assignForm.due_date}
                    onChange={e => setAssignForm(f => ({ ...f, due_date: e.target.value }))} />
                </div>
                <div className="fg">
                  <label className="fl">Max Score</label>
                  <input className="li" type="number" min="1" max="1000"
                    value={assignForm.max_score}
                    onChange={e => setAssignForm(f => ({ ...f, max_score: e.target.value }))} />
                </div>
              </div>
              <div className="fg" style={{ marginBottom: 0 }}>
                <label className="fl">Accepted File Types <span style={{ color: 'var(--subtle)', fontWeight: 400 }}>(comma-separated)</span></label>
                <input className="li" placeholder="PDF, DOCX, PPTX"
                  value={assignForm.accepted_file_types}
                  onChange={e => setAssignForm(f => ({ ...f, accepted_file_types: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button type="button" className="btn btn-o" onClick={() => { setModal(null); setSelAssign(null); }}>Cancel</button>
                <button type="submit" className="btn btn-p" disabled={saving}>
                  {saving ? 'Saving…' : (selAssign ? 'Save Changes' : 'Create Assignment')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          onDone={() => setToast(null)}
        />
      )}

    </>
  );
}
